/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTelephonyServersList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing Telephony Servers list data and DataGrid state.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { fetchTelephonyServers } from '../api';
import { useTelephonyServersColumns } from './useTelephonyServersColumns';

export function useTelephonyServersList() {
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || t('telephonyServers.defaultTenant');
  const availableColumns = useTelephonyServersColumns();

  const fetchTelephonyServersData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const response = await fetchTelephonyServers(params, tenantSlug, user?.id);
        return {
          data: response.data || [],
          pagination: response.pagination ? {
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          } : {
            page: params.page,
            limit: params.limit,
            total: 0,
          totalPages: 0
        }
      } as DataGridFetchResult;
    } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('telephonyServers.errors.loadFailed', 'Failed to load telephony servers');
        showError(message);
        throw error;
      }
    },
    [tenantSlug, user?.id, showError, t],
  );

  return useDataGrid({
    fetchData: fetchTelephonyServersData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'telephonyServers',
  });
}

